import { makeAutoObservable } from 'mobx';

import { axiosClient } from '../config/axios-client';
import { ALLOWED_AUDIO_FILE_TYPES, MAX_AUDIO_FILE_SIZE } from '../constants';
import { NotificationSectionKey } from '../model/settings/UserSettings';
import notificationStore from './NotificationStore';
import userSettingsStore from './UserSettingsStore';

const BASE_API_PATH = '/audio';

class AudioStore {
  private sounds = new Map<string, HTMLAudioElement>();

  constructor() {
    makeAutoObservable(this);
  }

  public preload = async (
    key: NotificationSectionKey,
    filename?: string,
  ): Promise<void> => {
    if (!filename) {
      filename = this.buildFilename(key);
    }
    axiosClient
      .get<Blob>(BASE_API_PATH, { params: { filename }, responseType: 'blob' })
      .then(response => {
        const audio = new Audio(URL.createObjectURL(response.data));
        audio.preload = 'auto';
        audio.load();
        this.sounds.set(key, audio);
      });
  };

  public upload = (files: File[], key: NotificationSectionKey): void => {
    const file = files[0];
    if (!file) return;

    if (!ALLOWED_AUDIO_FILE_TYPES.includes(file.type)) {
      notificationStore.showNotification({
        severity: 'warn',
        summary: 'Warning',
        detail: `'Audio format is not supported'`,
      });
      return;
    }

    if (file.size > MAX_AUDIO_FILE_SIZE) {
      notificationStore.showNotification({
        severity: 'warn',
        summary: 'Warning',
        detail: `Audio file size is more than ${MAX_AUDIO_FILE_SIZE / 1024}KB`,
      });
      return;
    }

    const newName = this.buildFilename(key, file.name.split('.').pop());
    const renamedFile = new File([file], newName, { type: file.type });

    const formData = new FormData();
    formData.append('file', renamedFile);

    axiosClient
      .put(BASE_API_PATH, formData)
      .then(response => {
        userSettingsStore.modifyNotificationSettings(
          { audio: response.data },
          key,
        );

        void this.preload(key, newName);

        notificationStore.showNotification({
          severity: 'success',
          summary: 'Success',
          detail: `Audio file ${file.name} successfully installed`,
        });
      })
      .catch(error => {
        notificationStore.showNotification({
          severity: 'warn',
          summary: 'Warning',
          detail: error.response.data.errorMessage || 'Error',
        });
      });
  };

  public play = (key: NotificationSectionKey): void => {
    const audio = this.sounds.get(key);
    if (!audio) return;

    audio.currentTime = 0;
    void audio.play().catch(() => null);
  };

  public reset = (filepath: string, key: NotificationSectionKey): void => {
    if (!filepath) {
      notificationStore.showNotification({
        severity: 'success',
        summary: 'Success',
        detail: `Audio file already set to default`,
      });
      return;
    }

    axiosClient
      .put(`${BASE_API_PATH}/reset`, null, { params: { filepath } })
      .then(response => {
        userSettingsStore.modifyNotificationSettings(
          { audio: response.data },
          key,
        );

        void this.preload(key);

        notificationStore.showNotification({
          severity: 'success',
          summary: 'Success',
          detail: `Audio file successfully set to default`,
        });
      });
  };

  public buildFilename = (
    key: NotificationSectionKey,
    extension = 'mp3',
  ): string => `${key.toLowerCase().replace('.', '-')}.${extension}`;
}

export const audioStore = new AudioStore();
export default audioStore;
